import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map, distinctUntilChanged} from 'rxjs/operators';
import {LayoutService} from '../../layout/layout.service';
import {Endpoint} from '../../layout/endpoint';

export interface EnvironmentAttributes {
  UseDescription: string;
  UseHint: string;
  ParentRequest: string;
  TimeZoneId: string | null;
  TimeZone: string | null;
  Request: string;
  Class: string;
  ObjectID: string;
  ObjectType: string;
  DisplayName: string;
  Description: string;
  Domain: string;
  ExplicitMember: string;
  Name: string;
  DataType: string;
  Multivalued: string;
  StringRegex: string;
  BoundObjectType: string;
  BoundAttributeType: string;
  Photo: string;
  UsageKeyword: string;
  ButtonPosition: string;
  ButtonTheme: string;
  ButtonType: string;
  SiteType: string;
  LinkedObject: string;
  Order: string;
  UrlID: string;
  ActionBarResources: string;
  Icon: string;
  TargetObjectType: string;
  AppliesToCreate: string;
  AppliesToWizard: string;
  AppliesToEdit: string;
  UIConfiguration: string;
  Configuration: string;
  UISync: string | null;
  StringResources: string;
  ParentOrder: string;
  CountXPath: string;
  DisplayInFooter: string;
  SearchScopeContext: string;
  SearchScope: string;
  SearchScopeResultObjectType: string;
  SearchScopeColumn: string;
  SortAttribute: string;
  SortDirection: string;
  SupportedLanguageCode: string;
  MaintenanceMode: string;
  Info: string;
  Email: string;
  DowntimeInfo: string;
  Creator: string;
  CreatedTime: string;
  Target: string;
  RequestParameter: string;
  ComputedMember: string;
  RequestStatus: string;
  RequestStatusDetail: string;
  DetailMessage: string;
  EntryTime: string;
  DetailLevel: string;
  Operation: string;
  Before: string | null;
  Content: string | null;
  ManagementPolicy: string;
  Timeline: string;
  Events: string | null;
  PrincipalSet: string | null;
  ResourceCurrentSet: string | null;
  ResourceFinalSet: string | null;
  Filter: string | null;
  ActionWorkflowDefinition: string | null;
  ActionWorkflowInstance: string | null;
  AuthenticationWorkflowInstance: string | null;
  AuthorizationWorkflowInstance: string | null;
  WorkflowDefinition: string | null;
  WorkflowStatus: string;
  WorkflowStatusDetail: string;
  RequestPhase: string;
  SyncConfigAttributeInclusion: string;
  FileName: string;
  DevEnvironment: string;
  Color: string;
  Theme: string;
  TextColor: string;
  SiteTitle: string;
  ApprovalStatus: string;
  ApprovalStatusMarker: string;
  ApprovalResponse: string;
  CommittedTime: string;
  Justification: string;
  HasCollateralRequest: string;
  Reason: string;
  GrantRight: string;
  PrimaryColor: string;
  AccentColor: string;
  WarnColor: string;
  DarkMode: string;
}

export interface EnvironmentObjectTypes {
  Resource: string;
  Approval: string;
  TimeZoneConfiguration: string | null;
  ObjectTypeDescription: string;
  AttributeTypeDescription: string;
  BindingDescription: string;
  ActionBarConfiguration: string;
  ViewConfiguration: string;
  HomepageConfiguration: string;
  NavigationBarConfiguration: string;
  SearchScopeConfiguration: string;
  SupportedLocaleConfiguration: string;
  UIConfiguration: string;
  UIModule: string;
  Request: string;
  Policy: string;
  Set: string;
  Event: string | null;
  SynchronizationRule: string | null;
  SynchronizationFilter: string | null;
  DomainConfiguration: string | null;
  LocaleConfituration: string;
  FilterScope: string | null;
  EmailTemplate: string | null;
  WorkflowDefinition: string | null;
  WorkflowInstance: string | null;
  MVData: string;
  Person: string;
}

export interface EnvironmentConfig {
  Type: string;
  Attributes: EnvironmentAttributes;
  ObjectTypes: EnvironmentObjectTypes;
}

const ENVIRONMENT_CONFIGS = {
  OnPrem: {
    Type: 'OnPrem',
    Attributes: {
      UseDescription: 'UseDescription',
      UseHint: 'UseHint',
      ParentRequest: 'ParentRequest',
      TimeZoneId: 'TimeZoneId',
      TimeZone: 'TimeZone',
      Request: 'Request',
      Class: 'Class',
      ObjectID: 'ObjectID',
      ObjectType: 'ObjectType',
      DisplayName: 'DisplayName',
      Description: 'Description',
      Domain: 'Domain',
      ExplicitMember: 'ExplicitMember',
      Name: 'Name',
      DataType: 'DataType',
      Multivalued: 'Multivalued',
      StringRegex: 'StringRegex',
      BoundObjectType: 'BoundObjectType',
      BoundAttributeType: 'BoundAttributeType',
      Photo: 'Photo',
      UsageKeyword: 'UsageKeyword',
      ButtonPosition: 'ButtonPosition',
      ButtonTheme: 'ButtonTheme',
      ButtonType: 'ButtonType',
      SiteType: 'SiteType',
      LinkedObject: 'LinkedObject',
      Order: 'Order',
      UrlID: 'URLID',
      ActionBarResources: 'ActionBarResources',
      Icon: 'Icon',
      TargetObjectType: 'TargetObjectType',
      AppliesToCreate: 'AppliesToCreate',
      AppliesToWizard: 'AppliesToWizard',
      AppliesToEdit: 'AppliesToEdit',
      UIConfiguration: 'UIConfigurationData',
      Configuration: 'ConfigurationData',
      UISync: 'UISync',
      StringResources: 'StringResources',
      ParentOrder: 'ParentOrder',
      CountXPath: 'CountXPath',
      DisplayInFooter: 'DisplayInFooter',
      SearchScopeContext: 'SearchScopeContext',
      SearchScope: 'SearchScope',
      SearchScopeResultObjectType: 'SearchScopeResultObjectType',
      SearchScopeColumn: 'SearchScopeColumn',
      SortAttribute: 'SortAttribute',
      SortDirection: 'SortDirection',
      SupportedLanguageCode: 'SupportedLanguageCode',
      MaintenanceMode: 'MaintenanceMode',
      Info: 'Info',
      Email: 'Email',
      DowntimeInfo: 'DowntimeInfo',
      Creator: 'Creator',
      CreatedTime: 'CreatedTime',
      Target: 'Target',
      RequestParameter: 'RequestParameter',
      ComputedMember: 'ComputedMember',
      RequestStatus: 'RequestStatus',
      RequestStatusDetail: 'RequestStatusDetail',
      DetailMessage: 'Message',
      EntryTime: 'EntryTime',
      DetailLevel: 'DetailLevel',
      Operation: 'Operation',
      Before: null,
      Content: null,
      ManagementPolicy: 'ManagementPolicy',
      Timeline: 'Timeline',
      Events: null,
      PrincipalSet: 'PrincipalSet',
      ResourceCurrentSet: 'ResourceCurrentSet',
      ResourceFinalSet: 'ResourceFinalSet',
      Filter: 'Filter',
      ActionWorkflowDefinition: 'ActionWorkflowDefinition',
      ActionWorkflowInstance: 'ActionWorkflowInstance',
      AuthenticationWorkflowInstance: 'AuthenticationWorkflowInstance',
      AuthorizationWorkflowInstance: 'AuthorizationWorkflowInstance',
      WorkflowDefinition: 'WorkflowDefinition',
      WorkflowStatus: 'WorkflowStatus',
      WorkflowStatusDetail: 'WorkflowStatusDetail',
      RequestPhase: 'RequestPhase',
      SyncConfigAttributeInclusion: 'SyncConfig-attribute-inclusion',
      FileName: 'FileName',
      DevEnvironment: 'DevEnvironment',
      Color: 'Color',
      Theme: 'Theme',
      TextColor: 'TextColor',
      SiteTitle: 'SiteTitle',
      ApprovalStatus: 'ApprovalStatus',
      ApprovalStatusMarker: 'ApprovalStatusMarker',
      ApprovalResponse: 'ApprovalResponse',
      CommittedTime: 'CommittedTime',
      Justification: 'Justification',
      HasCollateralRequest: 'HasCollateralRequest',
      Reason: 'Reason',
      GrantRight: 'GrantRight',
      PrimaryColor: 'PrimaryColor',
      AccentColor: 'AccentColor',
      WarnColor: 'WarnColor',
      DarkMode: 'DarkMode',
    },
    ObjectTypes: {
      Resource: 'Resource',
      Approval: 'Approval',
      TimeZoneConfiguration: 'TimeZoneConfiguration',
      ObjectTypeDescription: 'ObjectTypeDescription',
      AttributeTypeDescription: 'AttributeTypeDescription',
      BindingDescription: 'BindingDescription',
      ActionBarConfiguration: 'ActionBarConfiguration',
      ViewConfiguration: 'ObjectVisualizationConfiguration',
      HomepageConfiguration: 'HomepageConfiguration',
      NavigationBarConfiguration: 'NavigationBarConfiguration',
      SearchScopeConfiguration: 'SearchScopeConfiguration',
      SupportedLocaleConfiguration: 'SupportedLocaleConfiguration',
      UIConfiguration: 'UIConfiguration',
      UIModule: 'UIModule',
      Request: 'Request',
      Policy: 'ManagementPolicyRule',
      Set: 'Set',
      Event: null,
      SynchronizationRule: 'SynchronizationRule',
      SynchronizationFilter: 'SynchronizationFilter',
      DomainConfiguration: 'DomainConfiguration',
      LocaleConfituration: 'SupportedLocaleConfiguration',
      FilterScope: 'FilterScope',
      EmailTemplate: 'EmailTemplate',
      WorkflowDefinition: 'WorkflowDefinition',
      WorkflowInstance: 'WorkflowInstance',
      MVData: 'mv-data',
      Person: 'Person',
    },
  },
  Cloud: {
    Type: 'Cloud',
    Attributes: {
      UseDescription: 'usedescription',
      UseHint: 'usehint',
      ParentRequest: 'parentrequest',
      Request: 'request',
      TimeZone: null,
      TimeZoneId: null,
      Class: 'class',
      ObjectID: 'id',
      ObjectType: 'objecttype',
      DisplayName: 'displayname',
      Description: 'description',
      Domain: 'domain',
      ExplicitMember: 'explicit',
      Name: 'name',
      DataType: 'datatype',
      Multivalued: 'multivalued',
      StringRegex: 'stringregex',
      BoundObjectType: 'boundobject',
      BoundAttributeType: 'boundattribute',
      Photo: 'photo',
      UsageKeyword: 'usagekeywords',
      ButtonPosition: 'position',
      ButtonTheme: 'theme',
      ButtonType: 'type',
      SiteType: 'sitetype',
      LinkedObject: 'linkedobject',
      Order: 'order',
      UrlID: 'urlid',
      ActionBarResources: 'actionbarresources',
      Icon: 'icon',
      TargetObjectType: 'targetobjecttype',
      AppliesToCreate: 'create',
      AppliesToWizard: 'wizard',
      AppliesToEdit: 'edit',
      UIConfiguration: 'configuration',
      Configuration: '',
      UISync: null,
      StringResources: 'stringresources',
      ParentOrder: 'parentorder',
      CountXPath: 'countfilter',
      DisplayInFooter: 'displayinfooter',
      SearchScopeContext: 'attributesearched',
      SearchScope: 'filter',
      SearchScopeResultObjectType: 'resultobjecttype',
      SearchScopeColumn: 'attributedisplayed',
      SortAttribute: 'sortattribute',
      SortDirection: 'sortdirection',
      SupportedLanguageCode: 'languagecode',
      MaintenanceMode: 'maintenancemode',
      Info: 'info',
      Email: 'email',
      DowntimeInfo: 'downtimeinfo',
      Creator: 'creator',
      CreatedTime: 'createdtime',
      Target: 'target',
      RequestParameter: 'requestparameter',
      ComputedMember: 'computedmember',
      RequestStatus: 'state',
      RequestStatusDetail: 'RequestStatusDetail',
      DetailMessage: 'Message',
      EntryTime: 'EntryTime',
      DetailLevel: 'DetailLevel',
      Operation: 'operation',
      Before: 'before',
      Content: 'content',
      ManagementPolicy: 'permissions',
      Timeline: 'timeline',
      Events: 'events',
      PrincipalSet: null,
      ResourceCurrentSet: null,
      ResourceFinalSet: null,
      Filter: null,
      ActionWorkflowDefinition: null,
      ActionWorkflowInstance: 'actionworkflowinstance',
      AuthenticationWorkflowInstance: 'authenticationworkflowinstance',
      AuthorizationWorkflowInstance: 'authorizationworkflowinstance',
      WorkflowDefinition: 'workflowdefinition',
      WorkflowStatus: 'workflowstatus',
      WorkflowStatusDetail: 'workflowstatusdetail',
      RequestPhase: 'requestphase',
      SyncConfigAttributeInclusion: 'syncconfig-attribute-inclusion',
      FileName: 'filename',
      DevEnvironment: 'devenvironment',
      Color: 'color',
      Theme: 'theme',
      TextColor: 'textcolor',
      SiteTitle: 'sitetitle',
      ApprovalStatus: 'approvalstatus',
      ApprovalStatusMarker: 'approvalstatusmarker',
      ApprovalResponse: 'approvalresponse',
      CommittedTime: 'committedtime',
      Justification: 'justification',
      HasCollateralRequest: 'hascollateralrequest',
      Reason: 'reason',
      GrantRight: 'grantright',
      PrimaryColor: 'primarycolor',
      AccentColor: 'accentcolor',
      WarnColor: 'warncolor',
      DarkMode: 'darkmode',
    },
    ObjectTypes: {
      Resource: 'resource',
      Approval: 'approval',
      TimeZoneConfiguration: null,
      ObjectTypeDescription: 'objectdescription',
      AttributeTypeDescription: 'attributedescription',
      BindingDescription: 'bindingdescription',
      ActionBarConfiguration: 'actionbarconfiguration',
      ViewConfiguration: 'viewconfiguration',
      HomepageConfiguration: 'homepageconfiguration',
      NavigationBarConfiguration: 'navigationbarconfiguration',
      SearchScopeConfiguration: 'searchconfiguration',
      SupportedLocaleConfiguration: 'localeconfiguration',
      UIConfiguration: 'uiconfiguration',
      UIModule: 'uimodule',
      Request: 'request',
      Policy: 'policy',
      Set: 'set',
      Event: 'event',
      SynchronizationRule: null,
      SynchronizationFilter: null,
      DomainConfiguration: null,
      LocaleConfituration: 'localeconfiguration',
      FilterScope: null,
      EmailTemplate: null,
      WorkflowDefinition: null,
      WorkflowInstance: 'workflowinstance',
      MVData: 'mv-data',
      Person: 'user',
    },
  },
};

@Injectable({
  providedIn: 'root',
})
export class EnvironmentConfigService {
  private currentConfig$ = new BehaviorSubject<EnvironmentConfig>(ENVIRONMENT_CONFIGS.OnPrem);

  constructor(private layoutService: LayoutService) {
    // Subscribe to endpoint changes and update configuration accordingly
    this.layoutService.endpoint$
      .pipe(distinctUntilChanged((prev, curr) => prev.isCloud() === curr.isCloud()))
      .subscribe((endpoint: Endpoint) => {
        const config = endpoint.isCloud() ? ENVIRONMENT_CONFIGS.Cloud : ENVIRONMENT_CONFIGS.OnPrem;
        this.currentConfig$.next(config);
      });
  }

  /**
   * Get current configuration as observable
   */
  getConfig$(): Observable<EnvironmentConfig> {
    return this.currentConfig$.asObservable();
  }

  /**
   * Get current configuration synchronously
   */
  getCurrentConfig(): EnvironmentConfig {
    return this.currentConfig$.getValue();
  }

  /**
   * Get attribute name for current environment
   */
  getAttributeName(key: keyof EnvironmentAttributes): string | null {
    const config = this.getCurrentConfig();
    return config.Attributes[key];
  }

  /**
   * Get object type name for current environment
   */
  getObjectTypeName(key: keyof EnvironmentObjectTypes): string | null {
    const config = this.getCurrentConfig();
    return config.ObjectTypes[key];
  }

  /**
   * Get all attributes for current environment
   */
  getAttributes(): EnvironmentAttributes {
    return this.getCurrentConfig().Attributes;
  }

  /**
   * Get all object types for current environment
   */
  getObjectTypes(): EnvironmentObjectTypes {
    return this.getCurrentConfig().ObjectTypes;
  }

  /**
   * Check if current environment is cloud
   */
  isCloud(): boolean {
    return this.getCurrentConfig().Type === 'Cloud';
  }

  /**
   * Get attribute name observable for reactive updates
   */
  getAttributeName$(key: keyof EnvironmentAttributes): Observable<string | null> {
    return this.currentConfig$.pipe(
      map(config => config.Attributes[key]),
      distinctUntilChanged(),
    );
  }

  /**
   * Get object type name observable for reactive updates
   */
  getObjectTypeName$(key: keyof EnvironmentObjectTypes): Observable<string | null> {
    return this.currentConfig$.pipe(
      map(config => config.ObjectTypes[key]),
      distinctUntilChanged(),
    );
  }
}
